import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Shield, TrendingUp, Users, Building2, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";
import { DISCLOSED_ENTITY } from "../lib/entity";

const DIAGRAM_SRC = "/diagrams/bypass-middleman.png";

/**
 * /bypass-the-middleman — educational explainer page.
 *
 * Linked from the landing-page hero quote. Currently shows a **styled placeholder**
 * frame where the user will drop in the final diagram artwork; the rest of the
 * page is the brand-aligned narrative copy that should ship with it.
 */
const BypassMiddlemanPage = () => (
  <div className="min-h-screen bg-obsidian text-platinum">
    {/* Top nav */}
    <nav className="sticky top-0 z-50 px-8 py-6 bg-obsidian/85 backdrop-blur-xl border-b border-gold/10 shadow-card">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Logo size="large" linkTo="/" />
        <div className="flex items-center gap-4">
          <Link to="/" className="text-platinum/70 hover:text-platinum transition-colors flex items-center gap-1.5 text-sm">
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <Link to="/register">
            <Button className="bg-gold hover:bg-gold-light text-navy-dark font-semibold shadow-md">
              Get Started
            </Button>
          </Link>
        </div>
      </div>
    </nav>

    {/* Hero — Principle #1 */}
    <section className="relative py-24 px-8">
      <div className="max-w-5xl mx-auto">
        <p className="uppercase tracking-[0.3em] text-sm mb-4 text-burgundy font-semibold">
          Principle #1 · EchoSense Capital Club
        </p>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold leading-tight">
          ByPass the Financial{" "}
          <span className="text-gold animate-gold-glow">MiddleMan</span>
        </h1>
        <p className="mt-6 text-lg md:text-xl text-platinum/80 leading-relaxed max-w-3xl font-serif">
          Bypassing the middleman is the only action we can take to earn the returns currently
          available to only the wealthy and informed — hedge funds, market makers, family offices.
          Learn this concept. Internalize it. It is the foundation of everything we do.
        </p>
      </div>
    </section>

    {/* The diagram — embedded with brand frame on the dark page */}
    <section className="px-8 pb-16">
      <div className="max-w-6xl mx-auto">
        <div
          className="relative rounded-2xl overflow-hidden border border-gold/30 bg-gradient-to-br from-cream/95 to-cream-light/95 p-2 shadow-[0_20px_80px_-20px_rgba(234,179,8,0.25)]"
          data-testid="bypass-diagram-frame"
        >
          {/* Corner accents — luxury frame language */}
          <span aria-hidden className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-gold/70 rounded-tl-sm" />
          <span aria-hidden className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-gold/70 rounded-tr-sm" />
          <span aria-hidden className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-gold/70 rounded-bl-sm" />
          <span aria-hidden className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-gold/70 rounded-br-sm" />

          <img
            src={DIAGRAM_SRC}
            alt="Bypass the Middleman — EchoSense Capital Club infographic"
            className="w-full h-auto rounded-xl block"
            loading="eager"
            data-testid="bypass-diagram-image"
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 px-2">
          <p className="text-platinum/55 text-xs italic">
            Source: EchoSense Capital Club · For educational use by Club members only.
          </p>
          <a
            href={DIAGRAM_SRC}
            download="EchoSense-Bypass-The-Middleman.png"
            className="inline-flex items-center gap-1.5 text-gold/80 hover:text-gold text-xs font-medium underline decoration-gold/40 hover:decoration-gold underline-offset-4 transition-colors"
            data-testid="bypass-diagram-download"
          >
            <Download size={12} /> Download high-resolution
          </a>
        </div>
      </div>
    </section>

    {/* Conceptual breakdown — two paths */}
    <section className="px-8 py-16 bg-surface-highlight/60 border-y border-gold/10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-burgundy uppercase tracking-[0.25em] text-xs font-semibold mb-3">
            The Two Paths
          </p>
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-platinum">
            Where the returns actually go
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Traditional path */}
          <div className="rounded-lg border border-burgundy/30 bg-burgundy/[0.06] p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-burgundy font-semibold mb-4">
              Traditional Retail Path
            </p>
            <h3 className="text-xl font-serif text-platinum mb-6">Returns Leak at Every Layer</h3>
            <ol className="space-y-3 text-platinum/80 text-sm leading-relaxed">
              <li className="flex gap-3">
                <span className="text-burgundy font-bold shrink-0">1.</span>
                <span><strong className="text-platinum">You</strong> — your capital starts here</span>
              </li>
              <li className="flex gap-3">
                <span className="text-burgundy font-bold shrink-0">2.</span>
                <span><strong className="text-platinum">Retail Broker</strong> — spreads, commissions, payment-for-order-flow</span>
              </li>
              <li className="flex gap-3">
                <span className="text-burgundy font-bold shrink-0">3.</span>
                <span><strong className="text-platinum">Market Maker</strong> — internalizes flow, captures the bid-ask</span>
              </li>
              <li className="flex gap-3">
                <span className="text-burgundy font-bold shrink-0">4.</span>
                <span><strong className="text-platinum">Hedge Fund / Family Office</strong> — the actual return is realized here</span>
              </li>
            </ol>
            <p className="mt-6 text-platinum/70 text-sm italic">
              By the time the asset reaches you, the upside has been priced out by everyone in the chain.
            </p>
          </div>

          {/* EchoSense path */}
          <div className="rounded-lg border border-gold/30 bg-gold/[0.06] p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-4">
              EchoSense Direct Path
            </p>
            <h3 className="text-xl font-serif text-platinum mb-6">Returns Preserved at the Source</h3>
            <ol className="space-y-3 text-platinum/85 text-sm leading-relaxed">
              <li className="flex gap-3">
                <span className="text-gold font-bold shrink-0">1.</span>
                <span><strong className="text-platinum">You</strong> — qualified investor</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gold font-bold shrink-0">2.</span>
                <span><strong className="text-platinum">EchoSense Introducer Gateway</strong> — compliance-verified handoff (no fee skim)</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gold font-bold shrink-0">3.</span>
                <span><strong className="text-platinum">Regulated Capital Partner</strong> — institutional venue, direct access</span>
              </li>
            </ol>
            <p className="mt-6 text-platinum/80 text-sm italic">
              The chain that historically gated these returns to the wealthy is collapsed —
              you reach the venue directly.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Pillars row */}
    <section className="px-8 py-16">
      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
        {[
          { icon: Shield, title: "Compliance-First", body: "Every introduction is gated by KYC/AML, accreditation, and a written data-transfer consent." },
          { icon: TrendingUp, title: "Institutional Access", body: "Direct line into regulated venues normally fenced off by minimums and gatekeepers." },
          { icon: Users, title: "Member-Only", body: "The Club is exclusive by design. Knowledge and structure are the only edge." },
        ].map((p) => (
          <div key={p.title} className="rounded-lg border border-gold/15 bg-surface-highlight p-6">
            <p.icon size={22} className="text-gold mb-3" />
            <h3 className="text-platinum font-serif text-lg mb-2">{p.title}</h3>
            <p className="text-platinum/70 text-sm leading-relaxed">{p.body}</p>
          </div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="px-8 pb-24">
      <div className="max-w-4xl mx-auto rounded-xl border border-gold/20 bg-gradient-to-br from-navy-light/60 to-navy/40 p-10 text-center">
        <Building2 size={32} className="text-gold mx-auto mb-4" />
        <h3 className="text-2xl md:text-3xl font-serif text-platinum mb-3">
          Ready to bypass the middleman?
        </h3>
        <p className="text-platinum/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          Begin your investor verification. The compliance pathway is the gate — once you're through,
          the direct line is open.
        </p>
        <Link to="/verification">
          <Button size="lg" className="bg-gold hover:bg-gold-light text-navy-dark font-semibold shadow-lg">
            Start Investor Qualification
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </Link>
      </div>
    </section>

    {/* Footer crumb */}
    <footer className="px-8 py-8 border-t border-gold/10 text-center space-y-2">
      <p className="text-platinum/40 text-xs tracking-wider uppercase">
        EchoSense Capital Club · Introducer-Only · Arms-Length Compliance
      </p>
      <p className="text-platinum/40 text-[11px] leading-relaxed">
        <span className="text-platinum/60 font-medium">{DISCLOSED_ENTITY.name}</span>
        <br />
        {DISCLOSED_ENTITY.address}
      </p>
    </footer>
  </div>
);

export default BypassMiddlemanPage;
